import { useEffect, useState } from 'preact/hooks'
import { Plus, X } from 'lucide-preact'
import { outboundWebhooksApi } from '@/api/outboundWebhooks'
import { useOutboundWebhookSubscriptions } from '@/hooks/useOutboundWebhooks'
import type {
    OutboundWebhookSubscriptionViewDto,
    OutboundWebhookDeliveryViewDto,
} from '@/types/outboundWebhooks'
import { OneTimeApiKeyModal } from '../components/OneTimeApiKeyModal'
import { DeliverySparkline } from './DeliverySparkline'
import { OutboundWebhookDetail } from './OutboundWebhookDetail'
import { OutboundWebhookForm, type OutboundWebhookFormValues } from './OutboundWebhookForm'

interface OutboundWebhooksPanelProps {
    clientCompanyId: string
    workspaces?: { Id: string; Name: string }[]
}

type Mode = { kind: 'view' } | { kind: 'create' } | { kind: 'edit'; subscription: OutboundWebhookSubscriptionViewDto }

/**
 * Outbound webhooks: master-detail. Destinations in the rail, the selected one's configuration and
 * delivery log filling the rest.
 *
 * The shape follows what every comparable product converged on (GitHub, Stripe, Svix): the delivery
 * log is scoped to ONE destination, because the question being asked is almost always "is this
 * endpoint healthy", not "show me everything".
 */
export function OutboundWebhooksPanel({ clientCompanyId, workspaces = [] }: OutboundWebhooksPanelProps) {
    const { subscriptions, loading, error, reload } = useOutboundWebhookSubscriptions(clientCompanyId)

    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [mode, setMode] = useState<Mode>({ kind: 'view' })
    const [saving, setSaving] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    const [revealedSecret, setRevealedSecret] = useState<string | null>(null)

    // Create and rotate both defer their refresh to here, so the list behind the modal only settles
    // once the person has said they have the secret.
    const acknowledgeSecret = () => {
        setRevealedSecret(null)
        void reload()
    }
    const [confirmRotate, setConfirmRotate] = useState<OutboundWebhookSubscriptionViewDto | null>(null)
    const [confirmDelete, setConfirmDelete] = useState<OutboundWebhookSubscriptionViewDto | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const [recentByDestination, setRecentByDestination] = useState<Record<string, OutboundWebhookDeliveryViewDto[]>>({})

    // Mirrors InboundWebhooksPanel: fall back to the id so an unknown workspace still renders
    // something addressable rather than blank.
    const workspaceName = (id: string) => workspaces.find(workspace => workspace.Id === id)?.Name ?? id

    // Select the first destination once the list arrives, so the panel never opens on a blank pane.
    useEffect(() => {
        if (!selectedId && subscriptions && subscriptions.length > 0) setSelectedId(subscriptions[0].Id)
    }, [subscriptions, selectedId])

    // Rail sparklines: one small fetch per destination. Bounded by the per-company cap of 10, and
    // the alternative — no health signal until you click — is what makes finding the broken one slow.
    useEffect(() => {
        if (!subscriptions?.length) return
        let cancelled = false
        void (async () => {
            const entries = await Promise.all(subscriptions.map(async subscription => {
                const response = await outboundWebhooksApi.getDeliveries(clientCompanyId, {
                    subscriptionId: subscription.Id, limit: 10,
                })
                return [subscription.Id, response.data?.Deliveries ?? []] as const
            }))
            if (!cancelled) setRecentByDestination(Object.fromEntries(entries))
        })()
        return () => { cancelled = true }
    }, [subscriptions, clientCompanyId])

    const selected = subscriptions?.find(s => s.Id === selectedId) ?? null

    const handleCreate = async (values: OutboundWebhookFormValues) => {
        setSaving(true)
        setFormError(null)
        const response = await outboundWebhooksApi.createSubscription(clientCompanyId, {
            Name: values.Name, Url: values.Url, Description: values.Description, WorkspaceId: values.WorkspaceId,
        })
        setSaving(false)

        if (response.error || !response.data) {
            setFormError(response.error ?? 'The destination could not be created.')
            return
        }
        setMode({ kind: 'view' })
        setSelectedId(response.data.Subscription.Id)
        // The one moment this value exists outside a send. Surface it in the same paint as everything
        // else here, and hold the list refresh until it is acknowledged — a finished-looking
        // destination row appearing behind the modal is what invites someone to click away from the
        // only copy of their secret.
        setRevealedSecret(response.data.Secret)
    }

    const handleEdit = async (values: OutboundWebhookFormValues) => {
        if (mode.kind !== 'edit') return
        setSaving(true)
        setFormError(null)
        const response = await outboundWebhooksApi.updateSubscription(clientCompanyId, mode.subscription.Id, {
            Name: values.Name,
            Url: values.Url,
            Description: values.Description,
            WorkspaceId: values.WorkspaceId,
            ClearWorkspaceFilter: values.ClearWorkspaceFilter,
        })
        setSaving(false)

        if (response.error) {
            setFormError(response.error)
            return
        }
        setMode({ kind: 'view' })
        await reload()
    }

    const handleTogglePause = async (subscription: OutboundWebhookSubscriptionViewDto) => {
        setActionError(null)
        const response = await outboundWebhooksApi.updateSubscription(clientCompanyId, subscription.Id, {
            Status: subscription.Status === 'Paused' ? 'Active' : 'Paused',
        })
        if (response.error) {
            setActionError(response.error)
            return
        }
        await reload()
    }

    const handleRotate = async (subscription: OutboundWebhookSubscriptionViewDto) => {
        setConfirmRotate(null)
        setActionError(null)
        const response = await outboundWebhooksApi.rotateSecret(clientCompanyId, subscription.Id)
        if (response.error || !response.data) {
            // Failing silently is the dangerous case here: the customer walks away believing the old
            // secret is dead when it is still live.
            setActionError(response.error ?? 'The signing secret could not be rotated.')
            return
        }
        setRevealedSecret(response.data.Secret)
    }

    const handleDelete = async (subscription: OutboundWebhookSubscriptionViewDto) => {
        setConfirmDelete(null)
        setActionError(null)
        const response = await outboundWebhooksApi.deleteSubscription(clientCompanyId, subscription.Id)
        if (response.error) {
            setActionError(response.error)
            return
        }
        setSelectedId(null)
        await reload()
    }

    if (loading && !subscriptions) return <PanelSkeleton />

    if (error && !subscriptions) {
        return (
            <div role="alert" className="p-8 text-sm text-red-600 dark:text-red-400">
                {error}
            </div>
        )
    }

    if (subscriptions?.length === 0 && mode.kind !== 'create') {
        return (
            <>
                <EmptyState onAdd={() => setMode({ kind: 'create' })} />
                <SecretModal secret={revealedSecret} onAcknowledge={acknowledgeSecret} />
            </>
        )
    }

    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
            {actionError && (
                <div
                    role="alert"
                    className="flex items-start gap-3 px-4 py-2.5 border-b border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 text-[13px] text-red-700 dark:text-red-300"
                >
                    <span className="flex-1">{actionError}</span>
                    <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={() => setActionError(null)}
                        className="shrink-0 rounded hover:text-red-900 dark:hover:text-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                    >
                        <X className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[272px_1fr] min-h-[520px]">

                {/* --- rail --- */}
                <div className="bg-slate-50 dark:bg-slate-900/40 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700 flex flex-col">
                    <div className="flex items-center justify-between px-4 pt-4 pb-2.5">
                        <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500">
                            Destinations
                        </span>
                        <button
                            type="button"
                            onClick={() => { setMode({ kind: 'create' }); setFormError(null) }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md bg-primary-500 text-green-950 hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900 transition-all duration-150"
                        >
                            <Plus className="w-3 h-3" aria-hidden="true" /> Add
                        </button>
                    </div>

                    <div className="flex flex-col gap-0.5 px-2 pb-3" role="tablist" aria-label="Webhook destinations">
                        {subscriptions?.map(subscription => {
                            const isSelected = subscription.Id === selectedId && mode.kind === 'view'
                            const isPaused = subscription.Status === 'Paused'
                            return (
                                <button
                                    key={subscription.Id}
                                    role="tab"
                                    type="button"
                                    aria-selected={isSelected}
                                    onClick={() => { setSelectedId(subscription.Id); setMode({ kind: 'view' }) }}
                                    className={`w-full text-left rounded-lg px-3 py-2.5 flex flex-col gap-1.5 border transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                                        isSelected
                                            ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                                            : 'border-transparent hover:bg-slate-100 dark:hover:bg-slate-700/50'
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        <span
                                            aria-hidden="true"
                                            className={`text-xs ${isPaused ? 'text-slate-400' : 'text-green-600 dark:text-green-400'}`}
                                        >
                                            {isPaused ? '⏸' : '●'}
                                        </span>
                                        <span className={`text-[13.5px] font-medium truncate ${isPaused ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>
                                            {subscription.Name}
                                        </span>
                                    </span>
                                    <DeliverySparkline deliveries={recentByDestination[subscription.Id] ?? []} />
                                    <span className="text-[11.5px] text-slate-400 dark:text-slate-500 truncate">
                                        {subscription.WorkspaceId ? `${workspaceName(subscription.WorkspaceId)} only` : 'All workspaces'}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                </div>

                {/* --- detail --- */}
                <div className="min-w-0">
                    {mode.kind === 'create' && (
                        <OutboundWebhookForm
                            workspaces={workspaces}
                            saving={saving}
                            error={formError}
                            onSubmit={handleCreate}
                            onCancel={() => { setMode({ kind: 'view' }); setFormError(null) }}
                        />
                    )}

                    {mode.kind === 'edit' && (
                        <OutboundWebhookForm
                            existing={mode.subscription}
                            workspaces={workspaces}
                            saving={saving}
                            error={formError}
                            onSubmit={handleEdit}
                            onCancel={() => { setMode({ kind: 'view' }); setFormError(null) }}
                        />
                    )}

                    {mode.kind === 'view' && selected && (
                        <OutboundWebhookDetail
                            clientCompanyId={clientCompanyId}
                            subscription={selected}
                            workspaces={workspaces}
                            onEdit={() => { setMode({ kind: 'edit', subscription: selected }); setFormError(null) }}
                            onRotate={() => setConfirmRotate(selected)}
                            onDelete={() => setConfirmDelete(selected)}
                            onTogglePause={() => void handleTogglePause(selected)}
                            onChanged={() => void reload()}
                        />
                    )}
                </div>
            </div>

            <SecretModal secret={revealedSecret} onAcknowledge={acknowledgeSecret} />

            {confirmRotate && (
                <ConfirmDialog
                    title="Rotate signing secret?"
                    body={`${confirmRotate.Name} will start rejecting our requests the moment you do this, until whoever maintains it updates their copy. Deliveries in flight will fail.`}
                    confirmLabel="Rotate secret"
                    onConfirm={() => void handleRotate(confirmRotate)}
                    onCancel={() => setConfirmRotate(null)}
                />
            )}

            {confirmDelete && (
                <ConfirmDialog
                    title={`Delete ${confirmDelete.Name}?`}
                    body="Leads will stop being sent to this endpoint immediately. Its delivery history stays readable so you can still see what was sent."
                    confirmLabel="Delete destination"
                    onConfirm={() => void handleDelete(confirmDelete)}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}
        </div>
    )
}

function SecretModal({ secret, onAcknowledge }: { secret: string | null; onAcknowledge: () => void }) {
    return (
        <OneTimeApiKeyModal
            isOpen={Boolean(secret)}
            apiKey={secret ?? ''}
            title="Save your signing secret"
            onAcknowledge={onAcknowledge}
        />
    )
}

/**
 * The one place a modal earns itself here: both actions are destructive, immediate, and invisible
 * afterwards, so they need a deliberate stop rather than an inline control someone can hit by reflex.
 */
function ConfirmDialog({
    title, body, confirmLabel, onConfirm, onCancel,
}: {
    title: string
    body: string
    confirmLabel: string
    onConfirm: () => void
    onCancel: () => void
}) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onKeyDown={e => { if (e.key === 'Escape') onCancel() }}
        >
            <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 flex flex-col gap-3.5">
                <h4 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h4>
                <p className="text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">{body}</p>
                {/*
                  Initial focus sits on Cancel rather than on the destructive action: the dialog would
                  otherwise open one stray Enter away from deleting a destination.
                */}
                <div className="flex gap-2 pt-1">
                    <button
                        type="button"
                        onClick={onConfirm}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-red-300 dark:border-red-500/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 transition-colors duration-150"
                    >
                        {confirmLabel}
                    </button>
                    <button
                        type="button"
                        autoFocus
                        onClick={onCancel}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    )
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-16 flex flex-col items-center gap-3 text-center">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">No outbound webhooks</h3>
            <p className="text-[12.5px] text-slate-500 dark:text-slate-400 max-w-[46ch] leading-relaxed">
                Send every lead to an external system the moment it finishes processing — a partner's
                SDR service, an internal warehouse, an automation tool. You'll get a signing secret so
                the receiver can verify the request came from us.
            </p>
            <button
                type="button"
                onClick={onAdd}
                className="mt-1 inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg bg-primary-500 text-green-950 hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 transition-all duration-150"
            >
                <Plus className="w-4 h-4" aria-hidden="true" /> Add a destination
            </button>
        </div>
    )
}

function PanelSkeleton() {
    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 flex flex-col gap-3" aria-hidden="true">
            {[38, 82, 66, 74, 52].map((width, i) => (
                <div
                    key={i}
                    className="h-3 rounded bg-slate-100 dark:bg-slate-700/60 motion-safe:animate-pulse"
                    style={{ width: `${width}%` }}
                />
            ))}
        </div>
    )
}
